import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SideBar from 'Components/SideBar';
import Pagination from 'Components/Pagination';
import CommonModal from 'Components/CommonModal';
import AdminOrderIssueModal from 'Components/AdminOrderIssueModal';
import AdminOrderDetailModal from 'Components/AdminOrderDetailModal';
import UserSearchModal from 'Components/UserSearchModal';
import {
  catchError,
  changeState,
  formatWon,
  maskingInfo,
} from 'JS/common';
import { getAdminOrders } from 'JS/API';
import { adminOrderStatusArr, adminOrderStatusColor } from 'JS/array';
import { getCookie } from 'JS/cookie';

const AdminOrder = () => {
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });
  const [list, setList] = useState([]);

  // 필터
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchedUserPk, setSearchedUserPk] = useState(null);
  const [searchedUserLabel, setSearchedUserLabel] = useState('');

  // 모달
  const [userSearchModal, setUserSearchModal] = useState(false);
  const [issueModal, setIssueModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);

  // 알림
  const [alert, setAlert] = useState('');
  const [alertBox, setAlertBox] = useState({
    mode: '',
    context: '',
    bool: false,
  });

  const navigate = useNavigate();
  const preventRef = useRef(false);

  const fetchList = async () => {
    if (preventRef.current) return;
    preventRef.current = true;
    setTimeout(() => {
      preventRef.current = false;
    }, 200);

    const result = await getAdminOrders({
      page: pageInfo.page,
      limit: pageInfo.limit,
      status: statusFilter,
      user_pk: searchedUserPk || undefined,
    });
    if (typeof result === 'object') {
      setList(result?.data?.data || []);
      changeState(
        setPageInfo,
        'totalPage',
        result?.data?.meta?.total_pages || 1
      );
    } else return catchError(result, navigate, setAlertBox, setAlert);
  };

  // UserSearchModal에서 선택된 회원 → user_pk 필터로 적용
  const applyUserFilter = user => {
    if (!user) return;
    setSearchedUserPk(user.user_pk || user.id);
    const name = user.user_name || user.name || '';
    const id = user.user_id || '';
    setSearchedUserLabel(
      `${name ? maskingInfo('name', name) : ''}${
        id ? ` (${maskingInfo('id', id)})` : ''
      }`.trim() || `pk: ${user.user_pk || user.id}`
    );
    changeState(setPageInfo, 'page', 1);
  };

  const clearUserFilter = () => {
    setSearchedUserPk(null);
    setSearchedUserLabel('');
    changeState(setPageInfo, 'page', 1);
  };

  const resetFilters = () => {
    setStatusFilter('all');
    clearUserFilter();
  };

  const formatDate = dateStr => {
    if (!dateStr) return '-';
    return dateStr.replace('T', ' ').substring(0, 16);
  };

  const handleRowClick = merchant_uid => {
    setSelectedUid(merchant_uid);
    setDetailModal(true);
  };

  const renderTableBody = () => {
    if (!list || list.length === 0) {
      return null;
    }

    return list.map(item => {
      const {
        merchant_uid,
        merchant_name,
        supply_price,
        vat,
        merchant_price,
        status,
        status_label,
        created_at,
        expired_at,
        user,
        user_pk,
      } = item;

      const userLabel = user
        ? `${maskingInfo('name', user.name || user.user_name || '')} (${maskingInfo(
            'id',
            user.user_id || ''
          )})`
        : `pk: ${user_pk}`;

      return (
        <tr
          key={merchant_uid}
          className='clickable'
          onClick={() => handleRowClick(merchant_uid)}>
          <td>{merchant_uid}</td>
          <td>{userLabel}</td>
          <td>{merchant_name}</td>
          <td className='num'>{formatWon(supply_price)}</td>
          <td className='num'>{formatWon(vat)}</td>
          <td className='num strong'>{formatWon(merchant_price)}</td>
          <td>
            <span className={`status-badge ${adminOrderStatusColor[status] || ''}`}>
              {status_label || adminOrderStatusArr[status] || status}
            </span>
          </td>
          <td>{formatDate(created_at)}</td>
          <td>{formatDate(expired_at)}</td>
        </tr>
      );
    });
  };

  useEffect(() => {
    document.title = '마크클라우드 관리자 > 추가결제 관리';
    if (!getCookie('adminMyToken')) {
      navigate('/', { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!issueModal && !detailModal) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageInfo.page,
    pageInfo.limit,
    statusFilter,
    searchedUserPk,
    issueModal,
    detailModal,
  ]);

  return (
    <>
      <div className='container'>
        <SideBar />
        <div className='content-wrap subscription'>
          <div className='topBar'>
            <h2>추가결제 관리</h2>
            <div className='filter-row'>
              <select
                value={pageInfo.limit}
                onChange={e =>
                  setPageInfo(prev => ({
                    ...prev,
                    page: 1,
                    limit: Number(e.target.value),
                  }))
                }>
                <option value={10}>10개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <select
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  changeState(setPageInfo, 'page', 1);
                }}>
                <option value='all'>상태: 전체</option>
                <option value='Pending'>결제 대기</option>
                <option value='Paid'>결제 완료</option>
                <option value='Cancelled'>취소</option>
                <option value='Expired'>기한 만료</option>
                <option value='Refunded'>환불 완료</option>
              </select>
              <button
                className='searchBtn'
                onClick={() => setUserSearchModal(true)}>
                회원 검색
              </button>
              <button className='resetBtn' onClick={resetFilters}>
                초기화
              </button>
              <button className='grantBtn' onClick={() => setIssueModal(true)}>
                + 결제건 발행
              </button>
            </div>
            {searchedUserPk && (
              <div className='active-filter-row'>
                <span className='filter-chip'>
                  회원: {searchedUserLabel}
                  <button className='chip-clear' onClick={clearUserFilter}>
                    ×
                  </button>
                </span>
              </div>
            )}
          </div>
          <div className='table-wrap'>
            <table>
              <colgroup>
                <col width='18%' />
                <col width='14%' />
                <col width='14%' />
                <col width='9%' />
                <col width='8%' />
                <col width='10%' />
                <col width='9%' />
                <col width='9%' />
                <col width='9%' />
              </colgroup>
              <thead>
                <tr>
                  <th>주문번호</th>
                  <th>회원</th>
                  <th>항목명</th>
                  <th>공급가</th>
                  <th>부가세</th>
                  <th>총액</th>
                  <th>상태</th>
                  <th>발행일</th>
                  <th>결제 가능 기한</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </table>
          </div>
          {pageInfo.totalPage > 1 && (
            <Pagination setPageInfo={setPageInfo} pageInfo={pageInfo} />
          )}
        </div>
        {alertBox.bool && (
          <CommonModal
            setModal={setAlertBox}
            modal={alertBox}
            okFn={() => {
              if (alert === 'logout') navigate('/');
              else return;
            }}
          />
        )}
      </div>
      {userSearchModal && (
        <UserSearchModal
          onClose={() => setUserSearchModal(false)}
          onSelect={applyUserFilter}
        />
      )}
      {issueModal && (
        <AdminOrderIssueModal
          onClose={() => setIssueModal(false)}
          onIssued={created => {
            setIssueModal(false);
            if (created?.merchant_uid) {
              setSelectedUid(created.merchant_uid);
              setDetailModal(true);
            }
          }}
        />
      )}
      {detailModal && selectedUid && (
        <AdminOrderDetailModal
          merchantUid={selectedUid}
          onClose={() => {
            setDetailModal(false);
            setSelectedUid(null);
          }}
          onMutate={() => {
            // 취소/환불 발생 시 목록 새로고침은 모달 close 시 useEffect가 처리
          }}
        />
      )}
    </>
  );
};

export default AdminOrder;
